<script lang="ts" setup>
import { ref, onMounted, computed, watch } from 'vue'
import IconExpenses from '@lahaus-roomie/roomie/src/assets/icons/expenses.svg'
import type { Props } from './types'
import { whatsappListingNumbers } from '@/config/whatsappListingNumbers'
import type { LocaleCode } from '@/config/locale-domains'

const props = defineProps<Props>()
const emit = defineEmits(['showCta', 'clickPersonalizePlanCta'])

const observableElementsVisibility = ref<{ [key: string]: boolean }>({})
const samPresentationSectionVisible = ref(false)

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const isSomeObservableElementVisible = computed(() => Object.values(observableElementsVisibility.value).some(value => value))

const setFixedCtaObserver = () => {
  const typologiesFromObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => (observableElementsVisibility.value[entry.target.className] = entry.isIntersecting))
  }, { threshold: 0.1 })

  for (const selector of props.observableSelectors) {
    const observableElement = document.querySelector(selector) as HTMLDivElement
    if (observableElement) {
      observableElementsVisibility.value[observableElement.className] = false
      typologiesFromObserver.observe(observableElement)
    }
  }

  const samPresentationSectionObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => (samPresentationSectionVisible.value = entry.isIntersecting))
  }, { threshold: 0.1 })
  const samPresentationSection = document.querySelector('#sam-presentation-section') as HTMLDivElement
  if (samPresentationSection) samPresentationSectionObserver.observe(samPresentationSection)
}

const showCta = computed(() => true)

watch(showCta, (newValue) => {
  emit('showCta', newValue)
})

onMounted(() => {
  setTimeout(() => setFixedCtaObserver())
})

const { t, locale } = useI18n()
const { $whatsappNumbers } = useNuxtApp()
const route = useRoute()
const slug = route.params.slug as string

const whatsappHref = computed(() => {
  const number = whatsappListingNumbers[slug] ?? ($whatsappNumbers as Record<LocaleCode, string>)[locale.value as LocaleCode]
  const text = encodeURIComponent(`Hola me gustaría más información sobre ${props.listing.name}`)
  return `https://wa.me/${number}?text=${text}`
})

</script>

<script lang="ts">
export default {
  name: 'PdFixedCtas'
}
</script>

<template>
  <div
    class="pd-fixed-ctas fixed right-0 flex flex-col z-10 transition-all duration-500 ease-in-out"
    :style="{
      bottom: showCta ? '0' : '-100%'
    }">
    <div class="w-screen bg-white py-16 px-24 md:py-14 md:px-40 lg:px-56 flex flex-col md:flex-row justify-center items-center md:justify-between shadow-elevation-light-level-8">
      <div class="flex md:hidden items-center mb-12 lg:mb-8">
        <span class="flex h-32 w-32 items-center justify-center flex-none rounded-circle border border-brand-300 fill-carbon-600 pointer-events-none px-6 py-6">
          <IconExpenses class="w-full h-full" />
        </span>

        <p class="ml-8 text-primary-800 text-16 font-medium lg:text-16 !leading-8">
          {{ t('valueProposition') }}
        </p>
      </div>

      <p class="text-16 hidden md:block">
        {{ t('description') }}
      </p>

      <a
        id="pd-bottom-cta-contact-constructor"
        class="roomie-button roomie-button--md roomie-button--primary roomie-button--full-width w-full md:max-w-md"
        :href="whatsappHref"
        target="_blank"
        rel="noopener noreferrer">
        <span class="roomie-button__content">Hablar con asesor</span>
      </a>
    </div>
  </div>
</template>

<style lang="scss">
  .pd-fixed-ctas {
    @apply border-t border-carbon-light-50;

    .whatsapp-fixed-cta-label {
      display: none;
    }
  }
</style>

<i18n src="./i18n.json" lang="json"></i18n>
